// Coaching.js
import React, { useState, useEffect } from 'react';
import {
  FiMail,
  FiFilter,
  FiUser,
  FiBriefcase,
  FiAward,
  FiUsers,
  FiX,
} from 'react-icons/fi';

import { useNavigate } from 'react-router-dom';
import './Coaching.css';
import Sidebar from '../Sidebar';
import { CallMessaging } from '../messaging/Messaging';
import { getStoredUser } from '../../utils/authSession';
import {
  API_BASE_URL,
  toastdisplay,
  createProfilePictureUser,
  fetchConversations,
  createGroupedConversationsSetter,
} from '../../utils/utils';
import { generateCoachProfileUrl } from '../../utils/hashids';
import CoachDashboard from './CoachDashboard';
import StudentDashboard from './StudentDashboard';
import ProfilePicture from '../../components/ProfilePicture';

const Coaching = () => {
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState([]);
  const [filteredCoaches, setFilteredCoaches] = useState([]);
  const [availableSchools, setAvailableSchools] = useState([]);
  const [availableInterests, setAvailableInterests] = useState([]);
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [messageFromCoach, setMessageFromCoach] = useState('');
  const [user, setUser] = useState(() => getStoredUser());
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });
  const [sessionExpired, setSessionExpired] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [isLoadingCoaches, setIsLoadingCoaches] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isStackedLayout, setIsStackedLayout] = useState(
    window.innerWidth <= 1024
  );
  const [mobilePane, setMobilePane] = useState('discover');

  // Messaging panel state for external control
  const [showMessagingPanel, setShowMessagingPanel] = useState(false);
  const [
    selectedConversationForMessaging,
    setSelectedConversationForMessaging,
  ] = useState(null);

  // Check if user is a coach or student
  const isCoach = user?.role?.name === 'coach';
  const isStudent =
    user?.role?.name === 'student' || user?.role?.name === 'admin';
  const canUseMessaging = Boolean(user?.email);

  useEffect(() => {
    const syncViewport = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsStackedLayout(window.innerWidth <= 1024);
    };
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, []);

  useEffect(() => {
    // Only fetch data if user is not a coach and not navigating to student dashboard
    if (canUseMessaging && !isCoach) {
      const loadConversations = async () => {
        try {
          const groupedConversations = await fetchConversations(user.email);
          setConversations(groupedConversations);
        } catch (err) {
          console.error('Error fetching conversations:', err);
          setConversations([]);
        }
      };

      const fetchCoaches = async () => {
        try {
          // Check if we have cached data that's still valid (2 minutes)
          const cachedData = localStorage.getItem('cachedCoaches');
          const cacheTimestamp = localStorage.getItem('cachedCoachesTimestamp');

          if (cachedData && cacheTimestamp) {
            const now = Date.now();
            const cacheAge = now - parseInt(cacheTimestamp);
            const twoMinutes = 2 * 60 * 1000; // 2 minutes in milliseconds

            if (cacheAge < twoMinutes) {
              // Use cached data
              const parsedData = JSON.parse(cachedData);
              setCoaches(parsedData.coaches);
              setFilteredCoaches(parsedData.coaches);
              setAvailableSchools(parsedData.schools);
              setAvailableInterests(parsedData.interests);
              return;
            }
          }

          // Set loading state
          setIsLoadingCoaches(true);

          const res = await fetch(`${API_BASE_URL}/api/coaches`);
          if (!res.ok) throw new Error('Failed to load coaches');
          const data = await res.json();

          // Use real coach data from backend
          const coachesData = data.coaches || [];
          setCoaches(coachesData);
          setFilteredCoaches(coachesData);

          // Extract unique schools from coaches using the correct backend field names
          const allSchools = coachesData.flatMap(
            (coach) => coach.specialized_schools || []
          );
          const uniqueSchools = [...new Set(allSchools)];
          setAvailableSchools(uniqueSchools);

          // Extract unique interests from coaches using the correct backend field names
          const allInterests = coachesData.flatMap(
            (coach) => coach.sectors_of_interest || []
          );
          const uniqueInterests = [...new Set(allInterests)];
          setAvailableInterests(uniqueInterests);

          // Cache the data for 2 minutes
          const cacheData = {
            coaches: coachesData,
            schools: uniqueSchools,
            interests: uniqueInterests,
          };
          localStorage.setItem('cachedCoaches', JSON.stringify(cacheData));
          localStorage.setItem('cachedCoachesTimestamp', Date.now().toString());
        } catch (err) {
          console.error('Error fetching coaches:', err);
        } finally {
          setIsLoadingCoaches(false);
        }
      };

      const fetchSchools = async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/api/tags`);
          if (!res.ok) throw new Error('Failed to load tags');
          const data = await res.json();
          if (Array.isArray(data)) {
            localStorage.setItem('schools', JSON.stringify(data));
          } else {
            localStorage.setItem('schools', JSON.stringify([]));
          }
        } catch (err) {
          console.error('Error fetching tags:', err);
          localStorage.setItem('schools', JSON.stringify([]));
        }
      };

      fetchSchools();
      loadConversations();
      fetchCoaches();
    }
  }, [canUseMessaging, isCoach, isStudent, user?.email]);

  // Filter coaches based on selected schools and interests
  useEffect(() => {
    let filtered = coaches;

    // Filter by schools
    if (selectedSchools.length > 0) {
      filtered = filtered.filter(
        (coach) =>
          coach.specialized_schools &&
          coach.specialized_schools.some((school) =>
            selectedSchools.includes(school)
          )
      );
    }

    // Filter by interests
    if (selectedInterests.length > 0) {
      filtered = filtered.filter(
        (coach) =>
          coach.sectors_of_interest &&
          coach.sectors_of_interest.some((interest) =>
            selectedInterests.includes(interest)
          )
      );
    }

    setFilteredCoaches(filtered);
  }, [selectedSchools, selectedInterests, coaches]);

  const handleSchoolFilter = (school) => {
    setSelectedSchools((prev) =>
      prev.includes(school)
        ? prev.filter((s) => s !== school)
        : [...prev, school]
    );
  };

  const handleInterestFilter = (interest) => {
    setSelectedInterests((prev) =>
      prev.includes(interest)
        ? prev.filter((i) => i !== interest)
        : [...prev, interest]
    );
  };

  const clearFilters = () => {
    setSelectedSchools([]);
    setSelectedInterests([]);
  };

  const hasActiveFilters =
    selectedSchools.length > 0 || selectedInterests.length > 0;
  const hasFilterOptions =
    availableSchools.length > 0 || availableInterests.length > 0;

  // Handle messaging panel close
  const handleMessagingPanelClose = () => {
    setShowMessagingPanel(false);
    setSelectedConversationForMessaging(null);
  };

  // Handle opening messaging panel with a specific coach
  const handleOpenMessagingWithCoach = (coach) => {
    // Find existing conversation with this coach
    const existingConversation = conversations.find(
      (conv) =>
        conv.email === coach.email ||
        conv.receiver_email === coach.email ||
        conv.name === `${coach.first_name} ${coach.last_name}`
    );

    if (existingConversation) {
      // If conversation exists, select it and open messaging panel
      setSelectedConversationForMessaging(existingConversation);
      setShowMessagingPanel(true);
    } else {
      // If no conversation exists, create a new one and open messaging panel
      const newConversation = {
        id: `temp_${Date.now()}`,
        name: `${coach.first_name} ${coach.last_name}`,
        email: coach.email,
        receiver_email: coach.email,
        avatar: coach.profile_picture,
        status: 'Online',
        messages: [],
        lastMessage: '',
        date: new Date().toISOString(),
        request_id: null,
      };

      // Add the new conversation to the list
      setConversations((prevConversations) => [
        newConversation,
        ...prevConversations,
      ]);

      // Select the new conversation and open messaging panel
      setSelectedConversationForMessaging(newConversation);
      setShowMessagingPanel(true);
    }
  };

  // If user is a coach, show the coach dashboard
  if (isCoach) {
    console.log('showing coach dashboard');
    return <CoachDashboard />;
  }

  // For students and admins, show coach listings with student dashboard on the right
  return (
    <div className="layout">
      <Sidebar />
      {canUseMessaging && (
        <CallMessaging
          showMessaging={!isMobile}
          conversations={conversations}
          userRole={user?.role.name}
          userEmail={user?.email}
          userId={user?.id}
          setConversations={setConversations}
          userName={user?.first_name + ' ' + user?.last_name}
          externalShowPanel={showMessagingPanel}
          externalSetShowPanel={setShowMessagingPanel}
          externalSelectedConversation={selectedConversationForMessaging}
          externalSetSelectedConversation={setSelectedConversationForMessaging}
          onClose={handleMessagingPanelClose}
        />
      )}
      {toast.show && (
        <div
          className={`toast-notification ${toast.success ? 'success' : 'error'}`}
        >
          <span>{toast.success ? '✔️' : '❌'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      <main className="content">
        <div
          className={`coaching-main-layout ${isStudent ? 'with-student-dashboard' : ''} ${
            isStudent && isStackedLayout
              ? mobilePane === 'sessions'
                ? 'is-sessions-pane'
                : 'is-discover-pane'
              : ''
          }`}
        >
          {isStudent && isStackedLayout && (
            <div className="coaching-mobile-panes" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={mobilePane === 'discover'}
                className={mobilePane === 'discover' ? 'active' : ''}
                onClick={() => setMobilePane('discover')}
              >
                Find a coach
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mobilePane === 'sessions'}
                className={mobilePane === 'sessions' ? 'active' : ''}
                onClick={() => setMobilePane('sessions')}
              >
                My sessions
              </button>
            </div>
          )}

          {/* Left side - Coach listings */}
          <div className="coaches-section">
            <header className="coaching-page-header">
              <div className="coaching-page-header-copy">
                <p className="coaching-kicker">Coaching</p>
                <h1>Find your coach</h1>
                <p className="coaching-page-lead">
                  Match with mentors who know your target schools and the
                  path you want to take.
                </p>
              </div>
              {!isLoadingCoaches && (
                <div className="coaching-results-pill" aria-live="polite">
                  <FiUsers />
                  <span>
                    {filteredCoaches.length}{' '}
                    {filteredCoaches.length === 1 ? 'coach' : 'coaches'}
                  </span>
                </div>
              )}
            </header>

            {hasFilterOptions && (
              <section
                className="coaching-filter-section"
                aria-label="Filter coaches"
              >
                <div className="coaching-filter-toolbar">
                  <div className="coaching-filter-toolbar-copy">
                    <h2>
                      <span className="coaching-filter-icon-wrap">
                        <FiFilter />
                      </span>
                      Refine your search
                    </h2>
                    <p>Select the schools and interests that matter to you.</p>
                  </div>
                  {hasActiveFilters && (
                    <button
                      type="button"
                      className="coaching-clear-filters"
                      onClick={clearFilters}
                    >
                      <FiX />
                      Clear all
                    </button>
                  )}
                </div>

                <div className="coaching-filter-grid">
                  {availableSchools.length > 0 && (
                    <div className="coaching-filter-group">
                      <div className="coaching-filter-label">
                        <span className="coaching-filter-label-icon schools">
                          <FiAward />
                        </span>
                        <span>Schools</span>
                        {selectedSchools.length > 0 && (
                          <span className="coaching-filter-count">
                            {selectedSchools.length}
                          </span>
                        )}
                      </div>
                      <div className="coaching-schools-filter-container">
                        {availableSchools.map((school, index) => (
                          <button
                            type="button"
                            key={`${school}-${index}`}
                            className={`coaching-filter-school-tag ${selectedSchools.includes(school) ? 'selected' : ''}`}
                            onClick={() => handleSchoolFilter(school)}
                            aria-pressed={selectedSchools.includes(school)}
                          >
                            {school}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {availableInterests.length > 0 && (
                    <div className="coaching-filter-group">
                      <div className="coaching-filter-label">
                        <span className="coaching-filter-label-icon interests">
                          <FiBriefcase />
                        </span>
                        <span>Interests</span>
                        {selectedInterests.length > 0 && (
                          <span className="coaching-filter-count">
                            {selectedInterests.length}
                          </span>
                        )}
                      </div>
                      <div className="coaching-schools-filter-container">
                        {availableInterests.map((interest, index) => (
                          <button
                            type="button"
                            key={`${interest}-${index}`}
                            className={`coaching-filter-school-tag ${selectedInterests.includes(interest) ? 'selected' : ''}`}
                            onClick={() => handleInterestFilter(interest)}
                            aria-pressed={selectedInterests.includes(interest)}
                          >
                            {interest}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {hasActiveFilters && (
                  <div className="coaching-active-filters">
                    {selectedSchools.map((school) => (
                      <button
                        type="button"
                        key={`active-school-${school}`}
                        className="coaching-active-chip school"
                        onClick={() => handleSchoolFilter(school)}
                      >
                        {school}
                        <FiX />
                      </button>
                    ))}
                    {selectedInterests.map((interest) => (
                      <button
                        type="button"
                        key={`active-interest-${interest}`}
                        className="coaching-active-chip interest"
                        onClick={() => handleInterestFilter(interest)}
                      >
                        {interest}
                        <FiX />
                      </button>
                    ))}
                  </div>
                )}
              </section>
            )}

            <div className="coaches-list">
              {isLoadingCoaches ? (
                <div className="loading-spinner-container">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading coaches...</span>
                  </div>
                  <p className="loading-text">Loading coaches...</p>
                </div>
              ) : filteredCoaches.length === 0 ? (
                <div className="no-coaches-message">
                  <div className="no-coaches-icon" aria-hidden="true">
                    <FiUsers />
                  </div>
                  <h3>
                    {hasActiveFilters
                      ? 'No coaches match these filters'
                      : 'No coaches available yet'}
                  </h3>
                  <p>
                    {hasActiveFilters
                      ? 'Try removing a school or interest to widen your search.'
                      : 'New coaches will appear here as soon as they join the platform.'}
                  </p>
                  {hasActiveFilters && (
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={clearFilters}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              ) : (
                filteredCoaches.map((coach, coachIndex) => (
                  <div
                    key={coach.id ?? coach.email ?? `coach-${coachIndex}`}
                    className="coach-card"
                  >
                    <ProfilePicture
                      user={createProfilePictureUser(coach)}
                      alt={`${coach.first_name} ${coach.last_name}`}
                      className="coach-avatar"
                      context="Coach listing card"
                    />
                    <div className="coach-info">
                      <div className="coach-content">
                        <h2 className="coach-name">
                          {coach.first_name} {coach.last_name}
                          {coach.is_new ? (
                            <span className="coach-new-badge">Nouveau</span>
                          ) : null}
                        </h2>
                        <div className="coach-card-meta">
                          {(coach.review_count || 0) > 0 ? (
                            <span className="coach-card-rating">
                              ★ {Number(coach.average_rating || 0).toFixed(1)}{' '}
                              ({coach.review_count})
                            </span>
                          ) : (
                            <span className="coach-card-rating is-new">
                              New coach on Liwaza
                            </span>
                          )}
                          {coach.price_from != null && coach.duration_from ? (
                            <span className="coach-card-price">
                              from €{Number(coach.price_from).toFixed(0)} ·{' '}
                              {coach.duration_from} min
                            </span>
                          ) : null}
                        </div>

                        {/* Coach tagline/expertise */}
                        {coach.tagline && (
                          <p className="coach-tagline">{coach.tagline}</p>
                        )}

                        {/* Schools where coach has expertise */}
                        {coach.specialized_schools?.length > 0 && (
                          <div className="coach-info-section">
                            <span className="info-label">
                              I specialize for the schools:
                            </span>
                            <div className="coach-schools">
                              {coach.specialized_schools?.map((school, index) => (
                                <span
                                  key={`${coach.id ?? coach.email ?? coachIndex}-school-${index}-${school}`}
                                  className="coach-school-tag"
                                >
                                  {school}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Sectors of Interest */}
                        {coach.sectors_of_interest &&
                          coach.sectors_of_interest.length > 0 && (
                            <div className="coach-info-section">
                              <span className="info-label">Interests:</span>
                              <div className="info-tags">
                                {coach.sectors_of_interest.map(
                                  (interest, index) => (
                                    <span
                                      key={`${coach.id ?? coach.email ?? coachIndex}-interest-${index}-${interest}`}
                                      className="coach-info-tag interest-tag"
                                    >
                                      {interest}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                        {/* Looking to Meet / Love to chat with */}
                        {coach.looking_to_meet &&
                          coach.looking_to_meet.length > 0 && (
                            <div className="coach-info-section">
                              <span className="info-label">
                                I'd love to chat with:
                              </span>
                              <div className="info-tags looking-to-meet-tags">
                                {coach.looking_to_meet.map((who, index) => (
                                  <span
                                    key={`${coach.id ?? coach.email ?? coachIndex}-chat-${index}-${who}`}
                                    className="coach-info-tag chat-tag"
                                  >
                                    {who}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>

                      <div className="coach-actions">
                        <button
                          className="btn btn-primary coach-profile-btn"
                          onClick={() =>
                            navigate(generateCoachProfileUrl(coach.id))
                          }
                        >
                          <FiUser />
                          Work with the coach
                        </button>
                        <button
                          className="btn-message-round"
                          onClick={() => handleOpenMessagingWithCoach(coach)}
                          title="Send a message to the coach"
                        >
                          <FiMail size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right side - Student Dashboard (only for students/admins) */}
          {isStudent && (
            <div className="student-coaching-dashboard-sidebar">
              <StudentDashboard onMessageCoach={handleOpenMessagingWithCoach} />
            </div>
          )}
        </div>

        {messageFromCoach && (
          <div className="liwaza-alert liwaza-alert-info">
            <FiMail className="liwaza-alert-icon" />
            <span>{messageFromCoach}</span>
          </div>
        )}
      </main>
    </div>
  );
};

export default Coaching;
